/*
 * gait-theme-shim.js
 * Locale-root and RTL compatibility for the legacy Trademark 1.7.3 scripts.
 * Loaded by layout/theme.liquid and layout/minimal.liquid after libs.js and
 * before script.js (docs/storefront/DESIGN.md sections 2 and 9).
 *
 * 1. RouterRouter.prototype.route (bundled in libs.js) matches routes against
 *    window.location.pathname. Under a secondary language the path starts with
 *    the locale root ("/ar/products/..."), so no product, cart or collection
 *    script would start. The wrapper strips Shopify.routes.root first and is
 *    otherwise identical to the original (same pattern conversion, same
 *    arguments, same `this`). With the root "/" it behaves exactly like libs.js.
 * 2. jQuery.ajaxPrefilter prefixes root-relative "/cart..." and "/search..."
 *    requests with the locale root, so AJAX responses (mini-cart, predictive
 *    search, cart.js) come back in the visitor's language.
 * 3. jQuery.fn.slick defaults to { rtl: true } when <html dir="rtl">, unless the
 *    caller sets rtl explicitly.
 * 4. Closes the language switcher dropdown (<details class="gx-lang__menu">)
 *    on outside click and on Escape.
 *
 * Every step is skipped safely when its dependency is missing. Pure helpers are
 * exposed on window.GxThemeShim for unit tests.
 */
(function (global) {
  'use strict';

  if (!global) return;

  // Same expressions as RouterRouter in assets/libs.js (search "RouterRouter=t()").
  var ESCAPE_REGEX = /[\-{}\[\]+?.,\\\^$|#\s]/g;
  var NAMED_PARAM = /(\(\?)?:\w+/g;
  var OPTIONAL_PARAM = /\((.*?)\)/g;
  var SPLAT_PARAM = /\*\w+/g;
  var ROUTE_STRIPPER = /^[#\/]|\s+$/g;
  var TRAILING_SLASH = /\/$/;
  var LOCALIZED_REQUEST = /^\/(?:cart|search)(?=$|[\/?.#])/;

  function isType(value, type) {
    return Object.prototype.toString.call(value) === '[object ' + type + ']';
  }

  function routeToRegExp(route) {
    var source = String(route)
      .replace(ESCAPE_REGEX, '\\$&')
      .replace(OPTIONAL_PARAM, '(?:$1)?')
      .replace(NAMED_PARAM, function (match, optional) { return optional ? match : '([^/?]+)'; })
      .replace(SPLAT_PARAM, '([^?]*?)');
    return new RegExp('^' + source + '(?:\\?([\\s\\S]*))?$');
  }

  /** "/ar" -> "/ar/", "" -> "/", "ar/" -> "/ar/". */
  function normalizeRoot(value) {
    var root = typeof value === 'string' && value ? value : '/';
    if (root.charAt(0) !== '/') root = '/' + root;
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  /** The storefront locale root, e.g. "/" or "/ar/". */
  function localeRoot(win) {
    var shopify = win && win.Shopify;
    var fromRoutes = shopify && shopify.routes && shopify.routes.root;
    var fromShop = win && win.shop && win.shop.rootUrl;
    return normalizeRoot(fromRoutes || fromShop || '/');
  }

  /** "/ar/products/x" with root "/ar/" -> "/products/x"; untouched when root is "/". */
  function stripLocaleRoot(pathname, root) {
    var path = typeof pathname === 'string' ? pathname : '';
    var prefix = normalizeRoot(root);
    if (prefix === '/') return path;
    var lowerPath = path.toLowerCase();
    var lowerPrefix = prefix.toLowerCase();
    if (lowerPath === lowerPrefix.slice(0, -1)) return '/';
    if (lowerPath.indexOf(lowerPrefix) === 0) return '/' + path.slice(prefix.length);
    return path;
  }

  /** The fragment RouterRouter matches against ("products/x", "" for home). */
  function routeFragment(pathname, root) {
    return stripLocaleRoot(pathname, root).replace(ROUTE_STRIPPER, '').replace(TRAILING_SLASH, '');
  }

  /** Callback arguments when the route matches, otherwise null. */
  function matchRoute(route, pathname, root) {
    var regExp = isType(route, 'RegExp') ? route : routeToRegExp(route);
    var fragment = routeFragment(pathname, root);
    if (!regExp.test(fragment)) return null;
    return regExp.exec(fragment).slice(1).map(function (param) {
      return param ? decodeURIComponent(param) : null;
    });
  }

  /** Prefix root-relative cart/search URLs with the locale root. */
  function prefixUrl(url, root) {
    var prefix = normalizeRoot(root);
    if (prefix === '/' || typeof url !== 'string' || !LOCALIZED_REQUEST.test(url)) return url;
    return prefix + url.slice(1);
  }

  /** Slick arguments with rtl: true added to an options object (or to a bare init call). */
  function withRtlDefault(args, isRtl) {
    var list = Array.prototype.slice.call(args || []);
    if (!isRtl) return list;
    var first = list[0];
    if (list.length === 0 || first === undefined) {
      list[0] = { rtl: true };
    } else if (first !== null && typeof first === 'object' && !Object.prototype.hasOwnProperty.call(first, 'rtl')) {
      list[0] = Object.assign({}, first, { rtl: true });
    }
    return list;
  }

  function documentIsRtl(win) {
    var doc = win && win.document;
    var html = doc && doc.documentElement;
    return !!(html && String(html.dir || (html.getAttribute && html.getAttribute('dir')) || '').toLowerCase() === 'rtl');
  }

  function patchRouter(win) {
    var Router = win.RouterRouter;
    if (typeof Router !== 'function' || !Router.prototype || Router.prototype.gxLocaleAware) return false;
    Router.prototype.route = function (route, name, callback) {
      if (isType(name, 'Function')) {
        callback = name;
        name = '';
      }
      if (!callback) callback = this.options && this.options[name];
      var location = win.location || {};
      var args = matchRoute(route, location.pathname, localeRoot(win));
      if (args && isType(callback, 'Function')) callback.apply(this, args);
      return this;
    };
    Router.prototype.gxLocaleAware = true;
    return true;
  }

  function patchAjax(win) {
    var $ = win.jQuery;
    if (!$ || typeof $.ajaxPrefilter !== 'function' || $.gxLocaleAware) return false;
    $.ajaxPrefilter(function (options) {
      if (options && typeof options.url === 'string') options.url = prefixUrl(options.url, localeRoot(win));
    });
    $.gxLocaleAware = true;
    return true;
  }

  function patchSlick(win) {
    var $ = win.jQuery;
    if (!$ || !$.fn || typeof $.fn.slick !== 'function' || $.fn.slick.gxRtlAware) return false;
    var original = $.fn.slick;
    var wrapped = function () {
      return original.apply(this, withRtlDefault(arguments, documentIsRtl(win)));
    };
    Object.keys(original).forEach(function (key) { wrapped[key] = original[key]; });
    wrapped.gxRtlAware = true;
    $.fn.slick = wrapped;
    return true;
  }

  function closeLanguageMenus(doc, except) {
    if (!doc || typeof doc.querySelectorAll !== 'function') return;
    Array.prototype.forEach.call(doc.querySelectorAll('details.gx-lang__menu[open]'), function (menu) {
      if (menu !== except) menu.removeAttribute('open');
    });
  }

  function enhanceLanguageMenus(win) {
    var doc = win.document;
    if (!doc || typeof doc.addEventListener !== 'function') return false;
    doc.addEventListener('click', function (event) {
      var target = event && event.target;
      var menu = target && typeof target.closest === 'function' ? target.closest('details.gx-lang__menu') : null;
      closeLanguageMenus(doc, menu);
    });
    doc.addEventListener('keydown', function (event) {
      if (!event || event.key !== 'Escape') return;
      var open = typeof doc.querySelector === 'function' ? doc.querySelector('details.gx-lang__menu[open]') : null;
      if (!open) return;
      open.removeAttribute('open');
      var summary = typeof open.querySelector === 'function' ? open.querySelector('summary') : null;
      if (summary && typeof summary.focus === 'function') summary.focus();
    });
    return true;
  }

  function install(win) {
    return {
      router: patchRouter(win),
      ajax: patchAjax(win),
      slick: patchSlick(win),
      languageMenus: enhanceLanguageMenus(win)
    };
  }

  global.GxThemeShim = {
    routeToRegExp: routeToRegExp,
    normalizeRoot: normalizeRoot,
    localeRoot: localeRoot,
    stripLocaleRoot: stripLocaleRoot,
    routeFragment: routeFragment,
    matchRoute: matchRoute,
    prefixUrl: prefixUrl,
    withRtlDefault: withRtlDefault,
    install: install,
    installed: install(global)
  };
})(typeof window !== 'undefined' ? window : this);
